import { X } from "lucide-react";
import { SetStateAction, useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
} from "@components/ui/dialog";
import CloseIcon from "@assets/svg/CloseIcon";
import LoadingAnimation from "@assets/svg/LoadingAnimation";
import { colors } from "@utils/theme";
import { searchSkill } from "@endpoints/skill.service";
import { updateAspiration } from "@endpoints/candidate.service";
import { setAspirationDetails } from "@store/actions/auth.action";
import { enqueueSnackbar } from "notistack";

interface Skill {
  _id: string;
  title: string;
  skillId?: string;
  description?: string;
  isReferenceSkill?: boolean;
  referenceSkillId?: string;
}

interface AddEditSkillModalProps {
  isEditing: boolean;
  setIsEditing: React.Dispatch<SetStateAction<boolean>>;
  aspirationDetails: any;
}

const AddEditSkillModal = ({
  isEditing,
  setIsEditing,
  aspirationDetails,
}: AddEditSkillModalProps) => {
  const dispatch = useDispatch();

  const [skills, setSkills] = useState<Skill[]>([]);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<Skill[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  // Seed the editable list from the saved aspiration whenever the modal opens
  useEffect(() => {
    if (isEditing) {
      setSkills(aspirationDetails?.skills ?? []);
      setQuery("");
      setSuggestions([]);
    }
  }, [isEditing, aspirationDetails]);

  // Debounced skill search
  useEffect(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setSuggestions([]);
      setIsLoadingSuggestions(false);
      return;
    }

    let active = true;
    setIsLoadingSuggestions(true);

    const handler = setTimeout(async () => {
      try {
        const { success, data } = await searchSkill(trimmed, {
          classification: aspirationDetails?.classification?.main,
          subClassification: aspirationDetails?.classification?.sub,
        });
        if (active && success && Array.isArray(data)) {
          setSuggestions(data);
        }
      } catch (error) {
        if (active) setSuggestions([]);
      } finally {
        if (active) setIsLoadingSuggestions(false);
      }
    }, 300);

    return () => {
      active = false;
      clearTimeout(handler);
    };
  }, [query]);

  const handleAddSkill = (skill: Skill) => {
    const alreadyAdded = skills.some((item) => item._id === skill._id);
    if (!alreadyAdded) {
      setSkills((prev) => [...prev, skill]);
    }
    setQuery("");
    setSuggestions([]);
  };

  const handleRemoveSkill = (id: string) => {
    setSkills((prev) => prev.filter((skill) => skill._id !== id));
  };

  const handleSave = async () => {
    if (!aspirationDetails?._id) return;

    setIsUpdating(true);
    try {
      const payload = {
        _id: aspirationDetails._id,
        skills: skills.map((skill) => {
          if (skill.isReferenceSkill || skill.referenceSkillId) {
            return {
              referenceSkillId: skill.referenceSkillId || skill._id,
              title: skill.title,
              description: skill.description,
            };
          }

          if (skill.skillId) {
            return {
              skillId: skill.skillId,
              title: skill.title,
              description: skill.description,
            };
          }

          return skill._id;
        }),
      };
      const { success, data } = await updateAspiration(payload);

      if (success) {
        dispatch(
          setAspirationDetails(data ?? { ...aspirationDetails, skills }),
        );
        enqueueSnackbar("Skills updated successfully!", { variant: "success" });
        setIsEditing(false);
      } else {
        enqueueSnackbar("Failed to update skills", { variant: "error" });
      }
    } catch (error: any) {
      enqueueSnackbar(error?.message || "An error occurred while saving", {
        variant: "error",
      });
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isEditing} onOpenChange={setIsEditing}>
      <DialogContent showCloseButton={false} className="bg-white !max-w-3xl">
        <div className="flex justify-between items-start mb-5">
          <div className="flex flex-col gap-0.5">
            <span className="font-semibold text-lg">Your Skills</span>
            <span className="font-medium text-xs text-light-black">
              Add the skills you'd like to be using in your next role.
            </span>
          </div>
          <DialogClose asChild>
            <button
              onClick={() => setIsEditing(false)}
              className="h-max p-2 rounded-md hover:bg-gray-50"
            >
              <CloseIcon className={"w-5 h-5"} />
            </button>
          </DialogClose>
        </div>
        <div className="bg-[#F9FAFB] p-5 rounded-md flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="font-medium text-xs">Search skills</label>
            <div className="relative">
              <input
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. JavaScript, React, Communication"
                autoComplete="off"
                className="border rounded-lg w-full px-3 h-[42px] text-xs font-medium outline-none focus:border-primary"
              />

              {query.trim().length >= 2 && (
                <div className="absolute z-20 mt-1 max-h-48 w-full overflow-auto rounded-md bg-white py-1 text-xs shadow-lg border">
                  {isLoadingSuggestions ? (
                    <div className="px-4 py-2 text-light-black">
                      Loading suggestions...
                    </div>
                  ) : suggestions.length > 0 ? (
                    suggestions.map((suggestion) => {
                      const isAdded = skills.some(
                        (item) => item._id === suggestion._id,
                      );
                      return (
                        <div
                          key={suggestion._id}
                          onClick={() => !isAdded && handleAddSkill(suggestion)}
                          className={`px-4 py-2 flex items-center justify-between ${
                            isAdded
                              ? "text-gray-400 cursor-default"
                              : "cursor-pointer hover:bg-gray-50"
                          }`}
                        >
                          {suggestion.title}
                          {isAdded && (
                            <span className="text-[10px]">Added</span>
                          )}
                        </div>
                      );
                    })
                  ) : (
                    <div className="px-4 py-2 text-light-black">
                      No suggestions found
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Selected skills */}
          <div className="flex flex-col gap-2">
            <label className="font-medium text-xs">Selected skills</label>
            {skills.length > 0 ? (
              <div className="flex gap-2 flex-wrap">
                {skills.map((skill) => (
                  <div
                    key={skill._id}
                    className="flex items-center gap-1.5 rounded-full border bg-white font-medium text-xs pl-4 pr-2 py-1"
                  >
                    {skill.title}
                    <button
                      type="button"
                      onClick={() => handleRemoveSkill(skill._id)}
                      className="hover:bg-gray-100 rounded-full p-0.5"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <span className="text-xs text-light-black">
                No skills added yet.
              </span>
            )}
          </div>
        </div>
        <DialogFooter className="mt-5 gap-3">
          <button
            type="button"
            onClick={() => setIsEditing(false)}
            className="font-semibold text-sm text-slate-700 border py-2 px-5 rounded-md hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            disabled={isUpdating}
            className="font-semibold text-sm bg-primary text-white py-2 px-5 rounded-md flex items-center gap-2 disabled:opacity-60"
          >
            {isUpdating && <LoadingAnimation color={colors.White} />}
            Save Changes
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddEditSkillModal;
